import React from 'react';
import {
    Autocomplete,
    TextField,
    TextFieldProps,
    Checkbox,
    TextFieldVariants,
} from '@mui/material';

interface AutoCompleteFormControlProps<T> {
    label: string;
    options: T[];
    value: T | T[] | null;
    onChange: (
        event: React.SyntheticEvent<Element, Event>,
        value: T | T[] | null,
        key: string
    ) => void;
    onInputChange?: (
        event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => void;
    getOptionLabel?: (option: string | T) => string;
    isOptionEqualToValue?: (option: T, value: T) => boolean;
    id: string;
    size?: TextFieldProps['size'];
    error?: boolean;
    helperText?: string;
    multiple?: boolean;
    name: string;
    disable?: boolean;
    freesolo?: boolean;
    minWidth?: string;
    disableClear?: boolean;
    required?: boolean;
    variant?: TextFieldVariants | undefined;
}

const AutoCompleteFormControl = <T extends {}>({
    label,
    options,
    value,
    onChange,
    onInputChange,
    getOptionLabel,
    isOptionEqualToValue,
    id,
    size = 'medium',
    error,
    helperText,
    multiple = false,
    name,
    disable = false,
    freesolo = false,
    minWidth,
    disableClear = false,
    required = false,
    variant = 'outlined',
}: AutoCompleteFormControlProps<T>) => {
    // Change the signature of handleChange
    const handleChange = (
        event: React.SyntheticEvent<Element, Event>,
        value: T | T[] | null,
        key: string
    ) => {
        if (multiple) {
            onChange(event, value as T[], key);
        } else {
            if (freesolo && value !== null && !options.includes(value as T)) {
                // If freesolo is true and the value is not in the options,
                // consider it as a new value entered in the text field
                // You can handle the new value differently here, such as adding it to the options
                console.log('New value entered:', value);
            } else {
                onChange(event, value as T, key);
            }
        }
    };

    const handleInputChange = (
        event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => {
        if (freesolo && onInputChange) {
            onInputChange(event);
        }
    };

    return (
        <Autocomplete
            freeSolo={freesolo}
            options={options}
            getOptionLabel={
                getOptionLabel ||
                (option =>
                    typeof option === 'string' ? option : String(option))
            }
            value={value || null}
            onChange={(event, value) =>
                handleChange(event, value as T | T[] | null, name)
            }
            disableClearable={disableClear}
            isOptionEqualToValue={
                isOptionEqualToValue || ((option, value) => option === value)
            } // Custom equality logic
            renderInput={params => (
                <TextField
                    {...params}
                    label={label}
                    name={label}
                    size={size}
                    error={error}
                    helperText={helperText}
                    variant={variant}
                    id={`autocomplete-${id}`}
                    required={required}
                    InputLabelProps={{
                        sx: {
                            '& .MuiInputLabel-asterisk': {
                                color: 'red',
                            },
                        },
                    }}
                    onChange={event => {
                        freesolo && handleInputChange(event);
                    }}
                />
            )}
            multiple={multiple}
            renderOption={(
                props: React.HTMLAttributes<HTMLLIElement>,
                option: T, // Adjusted to generic type T
                { selected }: { selected: boolean }
            ) => (
                <li
                    {...props}
                    id={
                        getOptionLabel ? getOptionLabel(option) : String(option)
                    }
                >
                    {multiple && <Checkbox checked={selected} />}
                    {getOptionLabel ? getOptionLabel(option) : String(option)}
                </li>
            )}
            disabled={disable}
            sx={{ minWidth: minWidth }}
        />
    );
};

export default AutoCompleteFormControl;
